import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  ScrollText, Upload, MessageSquare, Send, FileText,
  Loader2, Clock, CheckCircle, AlertTriangle, Eye, Download, Trash2
} from 'lucide-react';

const API_BASE = window.location.origin;

const CONFIDENCE_STYLE = (c) =>
  c >= 0.9 ? 'border-green-300 text-green-700 bg-green-50' :
  c >= 0.7 ? 'border-amber-300 text-amber-700 bg-amber-50' :
  'border-red-300 text-red-700 bg-red-50';

function CpPdfPreview({ cpId, onReupload }) {
  const [state, setState] = useState('loading');
  const [reuploading, setReuploading] = useState(false);
  const blobUrlRef = React.useRef(null);

  useEffect(() => {
    let cancelled = false;
    setState('loading');

    // Cleanup previous blob URL
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }

    const token = localStorage.getItem('access_token');
    fetch(`${API_BASE}/api/charter-parties/${cpId}/file`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then(r => {
        if (!r.ok) throw new Error('not found');
        return r.blob();
      })
      .then(blob => {
        if (cancelled) return;
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setState(url);
      })
      .catch(() => {
        if (!cancelled) setState('error');
      });

    return () => {
      cancelled = true;
      if (blobUrlRef.current) {
        URL.revokeObjectURL(blobUrlRef.current);
        blobUrlRef.current = null;
      }
    };
  }, [cpId]);

  if (state === 'loading') {
    return (
      <div className="flex items-center justify-center py-24" data-testid="cp-pdf-loading">
        <div className="text-center space-y-3">
          <Loader2 className="h-6 w-6 animate-spin mx-auto text-[#0A2463]" />
          <p className="text-xs text-slate-400">Loading document...</p>
        </div>
      </div>
    );
  }

  if (state === 'error') {
    const handleReupload = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setReuploading(true);
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/charter-parties/${cpId}/reupload`, form, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        // Re-fetch the preview
        setState('loading');
        const token = localStorage.getItem('access_token');
        const r = await fetch(`${API_BASE}/api/charter-parties/${cpId}/file`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!r.ok) throw new Error('failed');
        const blob = await r.blob();
        const url = URL.createObjectURL(blob);
        blobUrlRef.current = url;
        setState(url);
        if (onReupload) onReupload();
      } catch {
        setState('error');
      } finally {
        setReuploading(false);
      }
    };

    return (
      <div className="flex items-center justify-center py-16" data-testid="cp-pdf-unavailable">
        <div className="text-center space-y-3">
          <div className="w-12 h-12 mx-auto bg-slate-100 rounded-sm flex items-center justify-center">
            <FileText className="h-6 w-6 text-slate-300" />
          </div>
          <p className="text-xs font-medium text-slate-500">Document preview not available</p>
          <p className="text-[11px] text-slate-400">Re-upload the document to enable preview</p>
          <label
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-sm border border-[#0A2463] text-[#0A2463] hover:bg-[#0A2463]/5 cursor-pointer transition-colors"
            data-testid="cp-reupload-button"
          >
            {reuploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {reuploading ? 'Uploading...' : 'Re-upload Document'}
            <input type="file" accept=".pdf,.txt,.doc,.docx" className="hidden" onChange={handleReupload} disabled={reuploading} />
          </label>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col" data-testid="cp-pdf-preview">
      <iframe
        src={state}
        className="w-full border-0 rounded-sm"
        style={{ height: '600px' }}
        title="Charter Party Document"
        data-testid="cp-pdf-iframe"
      />
      <div className="flex items-center gap-2 mt-2 px-1">
        <Eye className="h-3 w-3 text-slate-400" />
        <span className="text-[10px] text-slate-400 flex-1">Inline document preview</span>
        <a
          href={state}
          download
          className="text-[10px] text-[#0A2463] hover:underline flex items-center gap-1"
          data-testid="cp-pdf-download"
        >
          <Download className="h-3 w-3" /> Download
        </a>
      </div>
    </div>
  );
}

export default function CharterPartiesPage() {
  const [parties, setParties] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [vesselName, setVesselName] = useState('');
  const [question, setQuestion] = useState('');
  const [qaLoading, setQaLoading] = useState(false);
  const [qaAnswer, setQaAnswer] = useState(null);

  useEffect(() => {
    loadParties();
  }, []);

  const loadParties = async () => {
    try {
      const { data } = await api.get('/charter-parties');
      setParties(data);
      if (data.length > 0 && !selected) loadDetail(data[0].id);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const loadDetail = async (id) => {
    try {
      const { data } = await api.get(`/charter-parties/${id}`);
      setSelected(data);
      setQaAnswer(null);
    } catch (e) { console.error(e); }
  };

  const handleUpload = async () => {
    const fileInput = document.getElementById('cp-file-input');
    if (!fileInput?.files?.[0] || !vesselName.trim() || uploading) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', fileInput.files[0]);
      form.append('vessel_name', vesselName);
      await api.post('/charter-parties/upload', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setVesselName('');
      fileInput.value = '';
      await loadParties();
    } catch (e) {
      if (e.response?.status === 409) {
        alert(e.response.data.detail || 'This document was already uploaded.');
      }
      console.error(e);
    }
    finally { setUploading(false); }
  };

  const handleAsk = async () => {
    if (!question.trim() || !selected) return;
    setQaLoading(true);
    try {
      const { data } = await api.post(`/charter-parties/${selected.id}/ask`, { question });
      setQaAnswer(data);
      setQuestion('');
    } catch (e) { console.error(e); }
    finally { setQaLoading(false); }
  };

  const extractedTerms = selected?.extracted_terms_json || {};
  const terms = selected?.terms || [];
  const groupedTerms = terms.reduce((acc, t) => {
    const cat = t.term_category || 'other';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(t);
    return acc;
  }, {});

  const handleDelete = async (cpId, vesselName) => {
    if (!window.confirm(`Delete charter party for "${vesselName}"? This will remove all extracted terms and Q&A history. This action cannot be undone.`)) return;
    try {
      await api.delete(`/charter-parties/${cpId}`);
      setSelected(null);
      await loadParties();
    } catch (e) {
      console.error(e);
      alert('Failed to delete charter party');
    }
  };

  return (
    <div className="p-6 md:p-8 space-y-6" data-testid="charter-parties-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tighter text-slate-950">Charter Parties</h1>
          <p className="text-sm text-slate-500 mt-1">AI-powered charter party intelligence & term extraction</p>
        </div>
      </div>

      {/* Upload Section */}
      <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
        <CardContent className="p-4">
          <div className="flex items-end gap-3">
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Vessel Name</label>
              <Input
                value={vesselName}
                onChange={(e) => setVesselName(e.target.value)}
                placeholder="e.g. LNG Kano"
                className="rounded-sm mt-1"
                data-testid="cp-vessel-input"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Charter Party Document</label>
              <input
                id="cp-file-input"
                type="file"
                accept=".pdf,.txt,.doc,.docx"
                className="mt-1 text-sm text-slate-500 file:mr-4 file:py-2 file:px-3 file:rounded-sm file:border file:border-slate-200 file:text-xs file:font-medium file:bg-white file:text-slate-700 hover:file:bg-slate-50"
                data-testid="cp-file-input"
              />
            </div>
            <Button
              onClick={handleUpload}
              disabled={uploading || !vesselName.trim()}
              data-testid="cp-upload-button"
              className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
            >
              {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
              {uploading ? 'Processing...' : 'Upload & Extract'}
            </Button>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <Skeleton className="h-96" />
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Left: Charter Party List */}
          <div className="lg:col-span-3 space-y-2">
            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 px-1">
              Documents ({parties.length})
            </p>
            {parties.map(cp => (
              <Card
                key={cp.id}
                onClick={() => loadDetail(cp.id)}
                data-testid={`cp-card-${cp.vessel_name}`}
                className={`cursor-pointer transition-all rounded-sm shadow-none border ${
                  selected?.id === cp.id ? 'border-[#0A2463] bg-slate-50' : 'border-slate-200 bg-white hover:border-slate-300'
                }`}
              >
                <CardContent className="p-3">
                  <p className="text-xs font-bold text-slate-900">{cp.vessel_name}</p>
                  <div className="flex items-center gap-2 mt-1">
                    {cp.cp_type && (
                      <Badge variant="outline" className="rounded-sm text-[9px] uppercase">{cp.cp_type}</Badge>
                    )}
                    {cp.counterparty && (
                      <span className="text-[10px] text-slate-400 truncate">{cp.counterparty}</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Right: Detail */}
          <div className="lg:col-span-9">
            {!selected ? (
              <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                <CardContent className="py-24 text-center">
                  <ScrollText className="h-12 w-12 mx-auto text-slate-200 stroke-[1px]" />
                  <p className="mt-4 text-sm text-slate-400">Select a charter party to view extracted terms</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Header */}
                <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <h2 className="text-lg font-bold text-slate-900">{selected.vessel_name}</h2>
                        <div className="flex gap-3 mt-1 text-xs text-slate-500">
                          {selected.cp_type && <span>Type: {selected.cp_type}</span>}
                          {selected.counterparty && <span>Counterparty: {selected.counterparty}</span>}
                          {selected.time_counting_basis && <span>Basis: {selected.time_counting_basis}</span>}
                        </div>
                      </div>
                      <div className="text-right space-y-1">
                        {selected.laytime_allowance_hours && (
                          <p className="text-xs"><span className="text-slate-400">Laytime:</span> <span className="font-mono font-bold">{selected.laytime_allowance_hours}h</span></p>
                        )}
                        {selected.demurrage_rate_daily && (
                          <p className="text-xs"><span className="text-slate-400">Demurrage:</span> <span className="font-mono font-bold">${parseFloat(selected.demurrage_rate_daily).toLocaleString()}/day</span></p>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          className="mt-2 rounded-sm text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700 text-[10px]"
                          data-testid="cp-delete-button"
                          onClick={() => handleDelete(selected.id, selected.vessel_name)}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete Charter Party
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                <Tabs defaultValue="terms">
                  <TabsList className="w-full rounded-sm h-9 bg-slate-100 p-0.5">
                    <TabsTrigger value="terms" className="rounded-sm text-xs flex-1" data-testid="tab-terms">
                      Extracted Terms ({terms.length})
                    </TabsTrigger>
                    <TabsTrigger value="document" className="rounded-sm text-xs flex-1" data-testid="tab-document">
                      <FileText className="h-3 w-3 mr-1" />Document
                    </TabsTrigger>
                    <TabsTrigger value="ask" className="rounded-sm text-xs flex-1" data-testid="tab-ask">
                      <MessageSquare className="h-3 w-3 mr-1" />Ask the Charter Party
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="terms">
                    <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                      <CardContent className="p-4 space-y-4">
                        {Object.entries(groupedTerms).map(([category, catTerms]) => (
                          <div key={category}>
                            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">
                              {category.replace(/_/g, ' ')}
                            </p>
                            <div className="space-y-1.5">
                              {catTerms.map(t => (
                                <div key={t.id} className="flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-sm">
                                  <span className="text-xs font-medium text-slate-700 w-36 truncate">{t.term_key}</span>
                                  <span className="text-xs font-mono text-slate-900 flex-1">
                                    {typeof t.term_value === 'object' ? JSON.stringify(t.term_value) : String(t.term_value)}
                                  </span>
                                  {t.confidence_score && (
                                    <Badge variant="outline" className={`rounded-sm text-[9px] ${CONFIDENCE_STYLE(parseFloat(t.confidence_score))}`}>
                                      {(parseFloat(t.confidence_score) * 100).toFixed(0)}%
                                    </Badge>
                                  )}
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                        {terms.length === 0 && extractedTerms?.terms && (
                          <div className="space-y-2">
                            {extractedTerms.terms.map((t, i) => (
                              <div key={i} className="flex items-center gap-3 px-3 py-2 border border-slate-100 rounded-sm">
                                <Badge variant="outline" className="rounded-sm text-[9px] uppercase tracking-wider w-32 justify-center">
                                  {t.category}
                                </Badge>
                                <span className="text-xs font-medium text-slate-700 w-28 truncate">{t.key}</span>
                                <span className="text-xs font-mono text-slate-900 flex-1">{JSON.stringify(t.value)}</span>
                                <Badge variant="outline" className={`rounded-sm text-[9px] ${CONFIDENCE_STYLE(t.confidence)}`}>
                                  {(t.confidence * 100).toFixed(0)}%
                                </Badge>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="document">
                    <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                      <CardContent className="p-4">
                        <CpPdfPreview key={selected.id} cpId={selected.id} onReupload={() => loadDetail(selected.id)} />
                      </CardContent>
                    </Card>
                  </TabsContent>

                  <TabsContent value="ask">
                    <Card className="bg-white border border-slate-200 rounded-sm shadow-none">
                      <CardContent className="p-4 space-y-4">
                        <div className="flex gap-2">
                          <Input
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAsk()}
                            placeholder="e.g. What is the demurrage rate? What are the NOR requirements?"
                            className="rounded-sm flex-1"
                            data-testid="cp-question-input"
                          />
                          <Button
                            onClick={handleAsk}
                            disabled={qaLoading || !question.trim()}
                            data-testid="cp-ask-button"
                            className="rounded-sm bg-[#0A2463] hover:bg-[#0A2463]/90 text-white"
                          >
                            {qaLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </Button>
                        </div>

                        {qaAnswer && (
                          <div className="p-4 border border-slate-200 rounded-sm bg-slate-50" data-testid="cp-qa-answer">
                            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Answer</p>
                            <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-wrap">{qaAnswer.answer}</p>
                          </div>
                        )}

                        {/* Q&A History */}
                        {selected.qa_history?.length > 0 && (
                          <div>
                            <Separator className="my-3" />
                            <p className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500 mb-2">Previous Questions</p>
                            <div className="space-y-2">
                              {selected.qa_history.map(q => (
                                <div key={q.id} className="p-3 border border-slate-100 rounded-sm">
                                  <p className="text-xs font-medium text-slate-700">{q.question}</p>
                                  <p className="text-xs text-slate-500 mt-1 line-clamp-3">{q.answer}</p>
                                  <p className="text-[10px] text-slate-300 mt-1 font-mono">
                                    {new Date(q.created_at).toLocaleString()}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  </TabsContent>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
